<template>
  <b-modal
    id="add-or-update-patient-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Add new patient
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <b-row>
              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="First Name" rules="required">
                  <b-form-group label-for="first_name">
                    <template #label>
                      <span>First Name<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="first_name"
                      v-model="new_patient.first_name"
                      placeholder="First name"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Last Name" rules="required">
                  <b-form-group label-for="last_name">
                    <template #label>
                      <span>Last Name<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="last_name"
                      v-model="new_patient.last_name"
                      placeholder="Last name"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Phone Number" rules="required|length:10">
                  <b-form-group label-for="phone">
                    <template #label>
                      <span>Phone Number<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="phone"
                      v-model="new_patient.phone"
                      placeholder="Phone number"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-group label="Date of Birth" label-for="date of birth">
                  <template #label>
                    <span>Date of Birth<span style="color: tomato">*</span></span>
                  </template>

                  <validation-provider
                    #default="{ errors }"
                    name="date of birth"
                    rules="required|length:10"
                  >
                    <b-input-group>
                      <cleave 
                        v-model="new_patient.date_of_birth"
                        :options="cleaveDateOptions"
                        class="form-control"
                        name="date of birth"
                        placeholder="YYYY-MM-DD"
                        autocomplete="off"
                        :raw="false"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Gender" rules="required">
                  <b-form-group label-for="gender">
                    <template #label>
                      <span>Gender<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-select
                      id="gender"
                      v-model="new_patient.gender"
                      placeholder="Gender"
                      :disabled="disableForm"
                      :options="genderOptions"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Region" rules="required">
                  <b-form-group label-for="region">
                    <template #label>
                      <span>Region<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-select
                      id="region"
                      v-model="new_patient.region"
                      placeholder="Region"
                      :disabled="disableForm"
                      :options="regionOptions"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Location" rules="required">
                  <b-form-group label-for="location">
                    <template #label>
                      <span>Location<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="location"
                      v-model="new_patient.location"
                      placeholder="Location"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Email Address" rules="email">
                  <b-form-group label-for="email">
                    <template #label>
                      <span>Email Address</span>
                    </template>

                    <b-form-input
                      id="email"
                      v-model="new_patient.email"
                      placeholder="Email address"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-if="!isReferenced"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="button"
                :disabled="disableForm"
                @click="onCreate('yes')"
              >
                Save & Proceed
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                Save & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect, BInputGroup,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker, BInputGroupAppend
} from 'bootstrap-vue'

import Cleave from 'vue-cleave-component'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: "",
  date_of_birth: "",
  location: "",
  region: ""
}

export default {
  components: {
    BOverlay,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BFormInvalidFeedback,
    BButton,
    BFormTextarea,
    BAlert,
    BCardText,
    BModal,
    BRow,
    BCol,
    BFormDatepicker,
    BInputGroupAppend,
    BFormSelect,

    vSelect,
    Cleave,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    isAddActive: {
      type: Boolean,
      required: true,
    },
    isReferenced: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      genderOptions: [
        { value: 'male', text: 'Male' },
        { value: 'female', text: 'Female' },
      ],
      regionOptions: [],
      new_patient: cloneDeep(rawData),

      cleaveDateOptions: {
        date: true,
        raw: true,
        delimiter: '-',
        datePattern: ['Y', 'm', 'd'],
        blocks: [4, 2, 2],
      },
    }
  },
  computed: {
    isUpdate(){
      return !!this.updateData
    },
    disableForm(){
      return !this.can("patients:CanAddNew")
    },
  },
  created(){
    this.fetchGeoData()
  },
  methods: {
    async onCreate(proceed){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.can("patients:CanAddNew")) {
          return
        }
        
        const response = await this.useJwt().mainService.patients.create(this.new_patient);

        if (proceed){
          const id = get(response, 'data.data.id');
          if (id){
            this.$router.push({ name: 'admin-patient-details', params: { id } })
            .catch(() => {})
          }
        }

        this.$emit("refetchData");
        this.$emit('update:is-add-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Patient added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchGeoData(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          geo_id: 2300660
        }
        
        const response = await this.useJwt().mainService.geoData.list(query);
        const data = get(response, 'data.data');

        this.regionOptions = data.map((reg) => ({
          value: reg.name,
          text: reg.name,
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.$emit("resetUpdateData")
      this.new_patient = {};
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
